<template>
    <div class="company-information">
        <div class="company-information__columns">
            <Card>
                <Heading
                    :labelTitle="$t.__('CompanyInformation.legal_information.title')"
                    variant="h2"
                    :prefixColoredIcon="{ icon: 'building-skyscraper', color: 'brand' }"
                />
                <CompanyInformationLoader v-if="isLoading" />
                <template v-else>
                    <CompanyInformationFormLocked
                        v-if="companyInformationLocked"
                        :data="companyInformation"
                        :countries="countries"
                        @update="handleCompanyInformationUpdate"
                    />
                    <CompanyInformationForm
                        v-else
                        :data="companyInformation"
                        :countries="countries"
                        @update="handleCompanyInformationUpdate"
                    />
                </template>
            </Card>
            <Card>
                <Heading
                    :labelTitle="$t.__('CompanyInformation.contact_information.title')"
                    variant="h2"
                    :prefixColoredIcon="{ icon: 'user-hexagon', color: 'brand' }"
                />
                <template v-if="isLoading">
                    <CompanyInformationLoader />
                </template>
                <CompanyContactForm v-else :data="companyInformation" />
            </Card>
        </div>
        <Card>
            <div class="company-information__address-card-header">
                <Heading
                    :labelTitle="$t.__('CompanyInformation.addresses.title')"
                    variant="h2"
                    :prefixColoredIcon="{ icon: 'location', color: 'brand' }"
                />
                <CompassButton
                    :label="$t.__('CompanyInformation.addresses.button')"
                    type="secondary"
                    :disabled="isPanelOpen"
                    @click="handleAddAddress"
                />
            </div>
            <CompassAlert
                type="neutral"
                :description="$t.__('CompanyInformation.addresses.alert')"
                customIcon="/ressources/logged/img/icon/svg/info-e-invoicing.svg"
                :showClose="false"
            />
            <CompanyInformationAddressList
                :addresses="addresses"
                :loading="isLoading"
                :isPanelOpen="isPanelOpen"
                @delete="refreshAddresses"
                @update="refreshAddresses"
                @panelOpen="isPanelOpen = true"
                @panelClose="isPanelOpen = false"
            />
        </Card>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { onBeforeRouteLeave } from 'vue-router';
import { Translator } from 'types/injects/translator.type';
import { PushToast } from 'types/injects/pushToast.type';
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import CompanyInformationLoader from 'vcomponents/preferences/CompanyInformation/CompanyInformationLoader.vue';
import CompanyInformationFormLocked from 'vcomponents/preferences/CompanyInformation/CompanyInformationFormLocked.vue';
import CompanyInformationForm from 'vcomponents/preferences/CompanyInformation/CompanyInformationForm.vue';
import CompanyContactForm from 'vcomponents/preferences/CompanyInformation/CompanyContactForm.vue';
import CompanyInformationAddressList from 'vcomponents/preferences/CompanyInformation/CompanyInformationAddressList.vue';
import { getAccountInformation } from 'api/account/account';
import { getCountries } from 'api/countries';
import { fetchAccountAddresses } from 'api/addressesService';

export interface LegalFrance {
    siren: string;
    siret: string;
    vat: string;
    company_type: string;
    ape_naf_code: string;
    rcs_immatriculation: string;
}

export interface CompanyData {
    country_code: string;
    legal_name: string;
    capital: string;
    legal_france: LegalFrance;
}

export interface Country {
    code: string;
    name: string;
}

type Address = Record<string, unknown>;

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const store = useStore();

const companyInformation = ref<CompanyData>({} as CompanyData);
const countries = ref<Country[]>([]);
const addresses = ref<Address[]>([]);
const isLoading = ref<boolean>(true);
const isPanelOpen = ref<boolean>(false);

const companyInformationLocked = computed(
    (): boolean => store.getters['corp/getCompanyInformationLocked'],
);

const handleCompanyInformationUpdate = (payload: CompanyData): void => {
    companyInformation.value = payload;
};

const refreshAddresses = (newAddresses: Address[]): void => {
    addresses.value = newAddresses;
};

const handleAddAddress = (): void => {
    isPanelOpen.value = true;
    store.dispatch('popupPanel/open', {
        key: 'addressForm',
        props: {
            address: null,
            isAccountAddress: true,
            callback: (): void => {
                isPanelOpen.value = false;
            },
        },
    });
};

onBeforeRouteLeave((): void => {
    store.dispatch('popupPanel/close', { key: 'addressForm' });
});
onMounted((): void => {
    const accountPromise = getAccountInformation()
        .then((response) => {
            companyInformation.value = response.data as CompanyData;
        })
        .catch((): void => {
            $pushToast({
                type: 'error',
                text: $t.__('CompanyInformation.fetch.error'),
            });
        });

    const countriesPromise = getCountries()
        .then((response) => {
            countries.value = (response.data as { data: Country[] }).data;
        })
        .catch((): void => {
            $pushToast({
                type: 'error',
                text: $t.__('CompanyInformation.countries.fetch.error'),
            });
        });

    const addressesPromise = fetchAccountAddresses()
        .then((response) => {
            addresses.value = response as Address[];
        })
        .catch((): void => {
            $pushToast({
                type: 'error',
                text: $t.__('CompanyInformation.addresses.fetch_error'),
            });
        });

    Promise.all([accountPromise, countriesPromise, addressesPromise]).finally((): void => {
        isLoading.value = false;
    });

    EventBus.on('add-address', ((item: Address): void => {
        addresses.value.push(item);
    }) as (...args: unknown[]) => void);

    EventBus.on('edit-address', ((address: Address): void => {
        addresses.value = addresses.value.map((item) => {
            if (item['id'] !== address['id']) {
                return item;
            }
            return { ...item, ...address };
        });
    }) as (...args: unknown[]) => void);
});

onBeforeUnmount((): void => {
    EventBus.off('add-address');
    EventBus.off('edit-address');
});
</script>

<style lang="scss" scoped>
.company-information {
    &__columns {
        align-items: start;
        display: flex;
        gap: var(--spacing-4);
        margin-bottom: 24px;

        @media screen and (max-width: 1199px) {
            align-items: stretch;
            flex-direction: column;
        }

        :deep() {
            .card-container {
                flex: 1;

                & + .card-container {
                    margin-top: 0;
                }
            }
        }
    }

    &__title {
        margin: 0 0 var(--spacing-3);
    }

    &__address-card {
        &-header {
            align-items: start;
            display: flex;
            justify-content: space-between;
        }
    }
}
</style>
